import { Button, InputLabel, TextInput } from '@components/Common';
import { ProductAlias } from '@api/queries/products';
import React, { useState } from 'react';
import { TFunction } from 'i18next';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';

const DeleteLink = styled.a`
  color: red;
`;

const DeletedAlias = styled.span`
  font-style: italic;
  color: red;
`;

interface Props {
  values: ProductAlias[];
  onChange: (newValues: ProductAlias[]) => void;
  onInputChanged: (inputValue: ProductAlias) => void;
}

const ProductAliases = ({ values, onChange, t, onInputChanged }: Props & WithTranslation) => {
  const onAliasRemoved = (alias: ProductAlias) => {
    if (confirm(t('product.confirm_delete_alias'))) {
      onChange(values.filter(v => v !== alias));
    }
  };

  const onAliasAdded = (newAlias: ProductAlias) => {
    onChange([...values, newAlias]);
  };

  const codes = values.map(v => v.code);

  return (
    <div>
      <InputLabel htmlFor="aliasCode">{t('product.aliases')}</InputLabel>
      <table>
        <tbody>
          {values.map(v => (
            <ProductAliasRow key={v.code} value={v} onRemove={() => onAliasRemoved(v)} t={t} />
          ))}
        </tbody>
        <tfoot>
          <ProductAliasForm onAdd={onAliasAdded} t={t} onInputChanged={onInputChanged} currentCodes={codes} />
        </tfoot>
      </table>
      <div />
    </div>
  );
};

const ProductAliasRow = ({
  value,
  onRemove,
  t
}: { value: ProductAlias; onRemove: () => void; t: TFunction }) => {
  return (
    <tr>
      <td>{value.code}</td>
      <td>{value.name}</td>
      <td>
        {!value.isDeleted ? (
          <DeleteLink onClick={onRemove}>x</DeleteLink>
        ) : (
          <DeletedAlias>{t('product.alias_is_deleted')}</DeletedAlias>
        )}
      </td>
    </tr>
  );
};

interface ProductAliasProps {
  currentCodes: string[];
  onAdd: (value: ProductAlias) => void;
  t: any;
  onInputChanged: (value: ProductAlias) => void;
}

const ProductAliasForm = ({ currentCodes, onAdd, t, onInputChanged }: ProductAliasProps) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const onAddClicked = () => {
    setCode('');
    setName('');
    onAdd({ code, name, isDeleted: false });
  };

  const onCodeChanged = e => {
    setCode(e.target.value);
    onInputChanged({ code: e.target.value, name, isDeleted: false });
  };

  const onNameChanged = e => {
    setName(e.target.value);
    onInputChanged({ code, name: e.target.value.trim, isDeleted: false });
  };

  const isDisabled = code.trim().length === 0 || currentCodes.indexOf(code.trim()) >= 0;

  const isEnter = n => n === 13;

  const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (isEnter(e.keyCode)) {
      e.preventDefault();
      if (!isDisabled) {
        onAddClicked();
      }
    }
  };

  return (
    <tr>
      <td>
        <TextInput
          type="text"
          id="aliasCode"
          placeholder={t('product.code') + ' *'}
          value={code}
          onChange={onCodeChanged}
          onKeyDown={onKeyDown}
        />
      </td>
      <td>
        <TextInput
          type="text"
          placeholder={t('product.name')}
          value={name}
          onChange={onNameChanged}
          onKeyDown={onKeyDown}
        />
      </td>
      <td>
        <Button type="button" disabled={isDisabled} onClick={onAddClicked} primary={true} small={false}>
          {t('add')}
        </Button>
      </td>
    </tr>
  );
};

export default withTranslation()(ProductAliases);
